import React, { useState, useEffect } from "react";
import {
  TextField,
  Box,
  IconButton,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { baseUrl } from "../../../utils/config";

const CarImageUploader = ({ onImagesChange, initialImages = [] }) => {
  const [selectedImages, setSelectedImages] = useState([]);
  const [deletedPhotoIds, setDeletedPhotoIds] = useState([]);

  const imagesSyncKey = initialImages.map((img) => img.id || img.url).join(",");

  useEffect(() => {
    if (initialImages && initialImages.length > 0) {
      const formattedInitial = initialImages.map((img) => {
        const fullUrl = img.url.startsWith("http")
          ? img.url
          : `${baseUrl}/image/auto/${img.url}`;
        return {
          file: null,
          previewUrl: fullUrl,
          isExisting: true,
          id: img.id,
          url: img.url, // сохраняем исходное имя файла для бэкенда
          isPrimary: img.is_primary === 1,
        };
      });
      setSelectedImages(formattedInitial);
    } else {
      setSelectedImages([]);
    }
    setDeletedPhotoIds([]); // Сбрасываем при смене авто
  }, [imagesSyncKey]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newImages = files.map((file, idx) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      isExisting: false,
      // Если это вообще первые картинки, делаем самую первую главной по умолчанию
      isPrimary: selectedImages.length === 0 && idx === 0,
    }));

    const updatedImages = [...selectedImages, ...newImages];
    setSelectedImages(updatedImages);
    e.target.value = "";

    triggerChange(updatedImages, deletedPhotoIds);
  };

  const handleRemoveImage = (indexToRemove, e) => {
    e.preventDefault();
    e.stopPropagation();

    const targetImage = selectedImages[indexToRemove];
    const updatedImages = selectedImages.filter(
      (_, index) => index !== indexToRemove,
    );

    let updatedDeletedIds = [...deletedPhotoIds];
    if (targetImage.isExisting) {
      // Если удалили старое фото, сохраняем его ID для отправки в photoToDelete
      updatedDeletedIds.push(targetImage.id);
      setDeletedPhotoIds(updatedDeletedIds);
    } else if (targetImage.previewUrl) {
      URL.revokeObjectURL(targetImage.previewUrl);
    }

    // Если удалили главное фото, назначаем новым главным первое оставшееся
    if (targetImage.isPrimary && updatedImages.length > 0) {
      updatedImages[0].isPrimary = true;
    }

    setSelectedImages(updatedImages);
    triggerChange(updatedImages, updatedDeletedIds);
  };

  // Метод для выбора главного фото кликом
  const handleSetPrimary = (indexToPrimary, e) => {
    e.preventDefault();
    e.stopPropagation();

    const updatedImages = selectedImages.map((img, index) => ({
      ...img,
      isPrimary: index === indexToPrimary,
    }));

    setSelectedImages(updatedImages);
    triggerChange(updatedImages, deletedPhotoIds);
  };

  const triggerChange = (images, deletedIds) => {
    if (onImagesChange) {
      // 1. Все новые файлы для отправки в photoToAdd
      const newFiles = images
        .filter((img) => !img.isExisting)
        .map((img) => img.file);

      // 2. Вычисляем имя главного фото (из новых файлов отправляем имя, из старых — url)
      const primaryImg = images.find((img) => img.isPrimary);
      let primaryPhotoName = null;
      if (primaryImg) {
        primaryPhotoName = primaryImg.isExisting
          ? primaryImg.url
          : primaryImg.file.name;
      }

      onImagesChange({
        newFiles,
        primaryPhotoName,
        photoToDelete: deletedIds,
      });
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        component="label"
        sx={{ position: "relative", display: "block", cursor: "pointer" }}>
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileChange}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            opacity: 0,
            cursor: "pointer",
            zIndex: 2,
          }}
        />
        <TextField
          fullWidth
          label="Фотографии автомобиля"
          variant="outlined"
          focused={selectedImages.length > 0}
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <CloudUploadIcon
                  color={selectedImages.length > 0 ? "primary" : "action"}
                />
              </InputAdornment>
            ),
          }}
          helperText="Кликните на звезду, чтобы сделать фото главным."
          sx={{
            pointerEvents: "none",
            "& .MuiInputBase-root": { cursor: "pointer" },
          }}
        />
      </Box>

      {selectedImages.length > 0 && (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mt: 2,
            p: 1.5,
            border: "1px dashed #ccc",
            borderRadius: 1,
            overflowX: "auto",
            whiteSpace: "nowrap",
            WebkitOverflowScrolling: "touch",
            "&::-webkit-scrollbar": { height: "6px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#ccc",
              borderRadius: "4px",
            },
          }}>
          {selectedImages.map((img, index) => (
            <Box
              key={index}
              sx={{
                position: "relative",
                width: 100,
                height: 100,
                flexShrink: 0,
                borderRadius: 1,
                overflow: "hidden",
                boxShadow: 2,
                border: img.isPrimary ? "2px solid #1976d2" : "none",
                "&:hover .actions-overlay": { opacity: 1 },
              }}>
              <img
                src={img.previewUrl}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />

              {/* Постоянная маленькая иконка звезды, если фото главное */}
              {img.isPrimary && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 2,
                    left: 2,
                    color: "#1976d2",
                    zIndex: 1,
                    filter: "drop-shadow(0px 1px 2px rgba(0,0,0,0.6))",
                  }}>
                  <StarIcon size="small" />
                </Box>
              )}

              {/* Overlay при наведении */}
              <Box
                className="actions-overlay"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  bgcolor: "rgba(0, 0, 0, 0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-around",
                  opacity: 0,
                  transition: "opacity 0.2s",
                  zIndex: 3,
                }}>
                <Tooltip title="Сделать главным">
                  <IconButton
                    size="small"
                    sx={{ color: "#fff" }}
                    onClick={(e) => handleSetPrimary(index, e)}>
                    {img.isPrimary ? (
                      <StarIcon sx={{ color: "#ffca28" }} />
                    ) : (
                      <StarBorderIcon />
                    )}
                  </IconButton>
                </Tooltip>

                <Tooltip title="Удалить">
                  <IconButton
                    size="small"
                    sx={{ color: "#ff4d4d" }}
                    onClick={(e) => handleRemoveImage(index, e)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default CarImageUploader;
